/**
 * Flash card phase — the study window before first-pass spelling.
 * Implements spec §4.1.
 *
 * What Eva sees:
 *   - All of this week's words at once, large, in a wrap-grid.
 *   - Tap any word to hear it.
 *   - Tap the ♪ next to a word that has an example sentence to hear it.
 *   - A calm horizontal bar at the top shrinks from full to empty over
 *     60s (non-numerical, no alarming countdown per spec §10).
 *   - An "I need more time" button opens a "Have you asked Mum or Dad?"
 *     modal; confirming adds 30s. She can extend multiple times.
 *   - No Pumpkin on this screen (spec §7: zero distractions).
 *
 * When the timer reaches zero we cancel any speech and navigate to the
 * first-pass intro (Pumpkin-led "what's next" beat), which then leads
 * into the first-pass screen. The intro replaces the §4.1 "ready?"
 * 2-second auto-advance with a button-paced explanation — better for
 * a 6yo who needs reading time and context.
 *
 * State precision: tracked in milliseconds for a smooth bar; tick rate
 * is 100ms (CSS transition smooths between ticks). No
 * requestAnimationFrame — overkill for a 60s countdown.
 *
 * Mid-session resume (spec §5.3) is handled at the SessionContext +
 * sessionPersist layer; this screen is incidentally restartable — on
 * resume Eva gets the full 60s timer again (we don't preserve flash-
 * card timing), which is actually a nice "have another look" beat.
 */
import { useCallback, useEffect, useRef, useState } from "react";

import { Button } from "@/components/Button";
import { Modal } from "@/components/Modal";
import { useNavigation } from "@/context/NavigationContext";
import { useSession } from "@/context/SessionContext";
import { api } from "@/lib/api";
import { cancelSpeech, speak } from "@/lib/speech";
import type { Settings, WordEntry } from "@/types/data";
import "./FlashCardScreen.css";

const INITIAL_TIME_MS = 60_000;
const EXTEND_MS = 30_000;
const TICK_MS = 100;

type ScreenState =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "ready"; words: WordEntry[]; settings: Settings };

export function FlashCardScreen() {
  const { navigate } = useNavigation();
  const { session, startSession } = useSession();
  const [state, setState] = useState<ScreenState>(() =>
    // If a PastWeeksScreen tap already populated SessionContext before
    // navigating here, jump straight to "ready" with that session —
    // no need to fetch.
    session !== null
      ? { kind: "ready", words: session.words, settings: session.settings }
      : { kind: "loading" },
  );
  const [timeLeftMs, setTimeLeftMs] = useState(INITIAL_TIME_MS);
  const [totalTimeMs, setTotalTimeMs] = useState(INITIAL_TIME_MS);
  const [extendOpen, setExtendOpen] = useState(false);
  const expiredRef = useRef(false);

  // Load week + settings only when we don't already have a session in
  // hand. The "Play Today" path arrives with an empty SessionContext
  // (HomeScreen clears it before navigating); the past-week-review
  // path arrives with the chosen week already seeded.
  useEffect(() => {
    if (session !== null) return; // PastWeeks pre-populated the session
    let cancelled = false;
    (async () => {
      const [weeksRes, settingsRes] = await Promise.all([
        api.listWeeks(),
        api.getSettings(),
      ]);
      if (cancelled) return;
      if (weeksRes.status !== 200 || !weeksRes.body) {
        setState({ kind: "error", message: "Couldn't load the words. Try again from home." });
        return;
      }
      if (settingsRes.status !== 200 || !settingsRes.body) {
        setState({ kind: "error", message: "Couldn't load settings. Try again from home." });
        return;
      }
      const current = weeksRes.body.current;
      if (current === null) {
        // Shouldn't normally happen — HomeScreen wouldn't expose Play
        // Today without a current week — but cover the race anyway.
        setState({ kind: "error", message: "No words for this week yet." });
        return;
      }
      setState({ kind: "ready", words: current.words, settings: settingsRes.body });
      startSession({
        weekId: current.id,
        words: current.words,
        settings: settingsRes.body,
        countsTowardProgress: true,
      });
    })();
    return () => {
      cancelled = true;
      cancelSpeech();
    };
    // session is intentionally read-once on mount — if it changes
    // mid-mount (it shouldn't) we'd want to keep whatever's on screen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startSession]);

  // Tick the timer once `state` is ready. The interval ID lives in a
  // ref so the cleanup runs on every re-render rather than only on
  // unmount; we set it up once.
  const intervalRef = useRef<number | null>(null);
  useEffect(() => {
    if (state.kind !== "ready") return;
    intervalRef.current = window.setInterval(() => {
      setTimeLeftMs((t) => Math.max(0, t - TICK_MS));
    }, TICK_MS);
    return () => {
      if (intervalRef.current !== null) window.clearInterval(intervalRef.current);
      intervalRef.current = null;
    };
  }, [state.kind]);

  // Drive the phase-end transition. Guarded by a ref so a delayed
  // re-render of timeLeftMs === 0 can't double-fire navigate.
  useEffect(() => {
    if (state.kind !== "ready") return;
    if (timeLeftMs > 0) return;
    if (expiredRef.current) return;
    expiredRef.current = true;
    cancelSpeech();
    navigate("phaseIntro:firstPass");
  }, [timeLeftMs, state.kind, navigate]);

  const handleWordTap = useCallback(
    (word: WordEntry) => {
      if (state.kind !== "ready") return;
      speak(word.spelling, state.settings);
    },
    [state],
  );

  const handleSentenceTap = useCallback(
    (word: WordEntry) => {
      if (state.kind !== "ready" || word.exampleSentence === null) return;
      speak(word.exampleSentence, state.settings);
    },
    [state],
  );

  const handleExtendConfirm = useCallback(() => {
    setTotalTimeMs((t) => t + EXTEND_MS);
    setTimeLeftMs((t) => t + EXTEND_MS);
    setExtendOpen(false);
  }, []);

  if (state.kind === "loading") {
    return (
      <main className="flash" aria-busy="true">
        <p className="flash__loading">Getting your words ready…</p>
      </main>
    );
  }
  if (state.kind === "error") {
    return (
      <main className="flash">
        <p className="flash__error">{state.message}</p>
        <Button onClick={() => navigate("home")}>Back to home</Button>
      </main>
    );
  }

  const fraction = totalTimeMs === 0 ? 0 : timeLeftMs / totalTimeMs;

  return (
    <main className="flash">
      <div
        className="flash__timer"
        role="timer"
        aria-label="Time remaining to study"
      >
        <div
          className="flash__timer-bar"
          style={{ width: `${(fraction * 100).toFixed(2)}%` }}
        />
      </div>

      <ul className="flash__words">
        {state.words.map((word) => (
          <li key={word.id} className="flash__word-item">
            <button
              type="button"
              className="flash__word"
              onClick={() => handleWordTap(word)}
              aria-label={`Hear the word ${word.spelling}`}
            >
              {word.spelling}
            </button>
            {word.exampleSentence !== null && (
              <button
                type="button"
                className="flash__sentence"
                onClick={() => handleSentenceTap(word)}
                aria-label={`Hear a sentence for ${word.spelling}`}
              >
                ♪
              </button>
            )}
          </li>
        ))}
      </ul>

      <div className="flash__extend">
        <Button variant="secondary" onClick={() => setExtendOpen(true)}>
          I need more time
        </Button>
      </div>

      <Modal
        open={extendOpen}
        onClose={() => setExtendOpen(false)}
        titleId="extend-title"
      >
        <h2 id="extend-title" className="flash__modal-title">
          Have you asked Mum or Dad?
        </h2>
        <p className="flash__modal-body">
          We&rsquo;ll add 30 more seconds if you have.
        </p>
        <div className="flash__modal-actions">
          <Button variant="secondary" onClick={() => setExtendOpen(false)}>
            No
          </Button>
          <Button onClick={handleExtendConfirm}>Yes, I asked</Button>
        </div>
      </Modal>
    </main>
  );
}
